import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '@/context/AuthContext';
import '@/styles/opsIncidentFormStyles.scss';
import * as Sentry from '@sentry/react';

const tagOptions = [
    "Armed Robbery",
    "BreakIn",
    "Assault/Injury",
    "DamageToProperty",
    "DeathOnSite",
    "Theft"
];

const displayTagLabels = {
    "Armed Robbery": "Armed Robbery",
    "BreakIn": "Break In",
    "Assault/Injury": "Assault / Injury",
    "DamageToProperty": "Damage To Property",
    "DeathOnSite": "Death On Site",
    "Theft": "Theft"
};

/**
 * Fallback file storage configuration used before the server response arrives
 * or if the config endpoint is unavailable.
 */
const DEFAULT_FILE_STORAGE_CONFIG = {
    maxFileSizeBytes: 100 * 1024 * 1024, // 100 MB
    allowedExtensions: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip,.mp4,.mov,.avi,.mkv,.webm'
};

const OpsIncidentForm = () => {
    const now = new Date();
    // Default date to today, formatted as YYYY-MM-DD for the date input
    const todayStr = now.toISOString().split('T')[0];
    const { apiFetch } = useAuth();

    const [incidentDate, setIncidentDate] = useState(todayStr);
    const [properties, setProperties] = useState([]);
    const [property, setProperty] = useState('');
    const [tags, setTags] = useState([]);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    // File storage configuration fetched from the server so that accepted extensions and
    // size limits stay in sync with the backend without manual updates on the client.
    const [fileStorageConfig, setFileStorageConfig] = useState(DEFAULT_FILE_STORAGE_CONFIG);

    // Load properties from the API on mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active !== false);
                    setProperties(active);
                    if (active.length > 0) setProperty(active[0].propertyName);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Silently fail — form still works with manual input
            }
        };
        fetchProperties();
    }, [apiFetch]);

    // Fetch file storage configuration from the server on mount
    useEffect(() => {
        const fetchFileStorageConfig = async () => {
            try {
                const response = await apiFetch('/api/Settings/file-storage-config');
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        setFileStorageConfig({
                            maxFileSizeBytes: data.data.maxFileSizeBytes ?? DEFAULT_FILE_STORAGE_CONFIG.maxFileSizeBytes,
                            allowedExtensions: data.data.allowedExtensions ?? DEFAULT_FILE_STORAGE_CONFIG.allowedExtensions
                        });
                    }
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Silently fall back to defaults
            }
        };
        fetchFileStorageConfig();
    }, [apiFetch]);

    // Human-readable max size label derived from server config (e.g. "100 MB")
    const maxFileSizeLabel = useMemo(
        () => `${Math.round(fileStorageConfig.maxFileSizeBytes / (1024 * 1024))} MB`,
        [fileStorageConfig]
    );

    // Accepted extensions string for the file input — kept in sync with the server config
    const acceptedExtensions = fileStorageConfig.allowedExtensions;
    const maxDate = todayStr;

    const handleTagChange = (e) => {
        const { value, checked } = e.target;
        if (checked) {
            setTags(prev => [...prev, value]);
        } else {
            setTags(prev => prev.filter(tag => tag !== value));
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files || []);
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        const iconMap = {
            pdf: '📄', doc: '📃', docx: '📃', xls: '📊', xlsx: '📊',
            ppt: '📑', pptx: '📑',
            jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
            txt: '📝', csv: '📝', zip: '📦',
            mp4: '🎬', mov: '🎬', avi: '🎬', mkv: '🎬', webm: '🎬'
        };
        return iconMap[ext] || '📁';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!incidentDate || !property || tags.length === 0) {
            toast.error("Please fill in all fields and select at least one tag.");
            return;
        }

        // Client-side file size validation against the server-configured limit
        const oversizedFiles = selectedFiles.filter(file => file.size > fileStorageConfig.maxFileSizeBytes);
        if (oversizedFiles.length > 0) {
            toast.error(`The following file(s) exceed the ${maxFileSizeLabel} size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return;
        }

        // Derive year, month name, and day from the chosen date
        const dateObj = new Date(incidentDate + 'T00:00:00'); // avoid UTC offset shifting day
        const year = dateObj.getFullYear().toString();
        const month = dateObj.toLocaleString('en-US', { month: 'long' });
        const day = dateObj.getDate();

        setLoading(true);

        const requestBody = {
            Year: year,
            Month: month,
            Day: day,
            PropertyName: property,
            Tags: tags,
            Description: description || null
        };

        try {
            const response = await apiFetch('/api/Ops/OperationIncidents', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                const incidentId = data.data?.incidentId;

                // Upload files if any were selected and we got an incident ID
                if (selectedFiles.length > 0 && incidentId) {
                    const formData = new FormData();
                    selectedFiles.forEach(file => formData.append('file', file));
                    if (description) formData.append('description', description);

                    try {
                        const uploadResponse = await apiFetch(
                            `/api/Ops/Incidents/${incidentId}/documents/upload`,
                            { method: 'POST', body: formData }
                        );
                        const uploadData = await uploadResponse.json();
                        if (uploadData.success) {
                            toast.success(`Incident saved with ${selectedFiles.length} file(s) uploaded!`);
                        } else {
                            toast.warning("Incident saved but file upload failed: " + (uploadData.message || "Unknown error"));
                        }
                    } catch (catchError) {
                        Sentry.captureException(catchError);
                        toast.warning("Incident saved but file upload encountered an error.");
                    }
                } else {
                    toast.success("Incident saved successfully!");
                }

                // Reset form
                setTags([]);
                setDescription('');
                setSelectedFiles([]);
                setIncidentDate(todayStr);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                toast.error(data.message || "Failed to save incident.");
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error("An error occurred while saving the incident.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="ops-incident-form" onSubmit={handleSubmit}>
            <h1>Operations Incident Form</h1>
            <div>
                <label htmlFor="incidentDate" style={{ display: 'block' }}>Incident Date:</label>
                <input
                    type="date"
                    id="incidentDate"
                    value={incidentDate}
                    max={maxDate}
                    onChange={e => setIncidentDate(e.target.value)}
                />
            </div>
            <div>
                <label htmlFor="property">Property:</label>
                <select
                    id="property"
                    value={property}
                    onChange={e => setProperty(e.target.value)}
                >
                    {properties.length > 0 ? (
                        properties.map(p => (
                            <option key={p.propertyID} value={p.propertyName}>{p.propertyName}</option>
                        ))
                    ) : (
                        <option value="">Loading properties...</option>
                    )}
                </select>
            </div>
            <div>
                <label>Tags:</label>
                <div>
                    {tagOptions.map(tag => (
                        <label key={tag} style={{ marginRight: '1em' }}>
                            <input
                                type="checkbox"
                                value={tag}
                                checked={tags.includes(tag)}
                                onChange={handleTagChange}
                            />
                            {displayTagLabels[tag]}
                        </label>
                    ))}
                </div>
            </div>
            <div>
                <label htmlFor="description">Description:</label>
                <textarea
                    id="description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Optional description of the incident..."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                />
            </div>
            <div className="file-upload-section">
                <label>Attach Files:</label>
                <div
                    className={`file-dropzone${isDragging ? ' dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept={acceptedExtensions}
                        hidden
                    />
                    <div className="dropzone-content">
                        <span className="dropzone-icon">📂</span>
                        <p className="dropzone-text">Drag &amp; drop files here or <span className="dropzone-browse">browse</span></p>
                        <p className="dropzone-hint">PDF, Word, Excel, PowerPoint, Images, CSV, ZIP, Video · Max size: {maxFileSizeLabel}</p>
                    </div>
                </div>
                {selectedFiles.length > 0 && (
                    <div className="file-list">
                        <div className="file-list-header">
                            <span>{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</span>
                            <button type="button" className="clear-all-btn" onClick={() => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Clear all</button>
                        </div>
                        {selectedFiles.map((file, idx) => (
                            <div key={idx} className="file-item">
                                <span className="file-icon">{getFileIcon(file.name)}</span>
                                <div className="file-details">
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                </div>
                                <button type="button" className="file-remove-btn" onClick={() => removeFile(idx)} title="Remove file">✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Incident"}
            </button>
        </form>
    );
};

export default OpsIncidentForm;